'use client';

import { useState, useRef, useEffect } from 'react';
import { useVideoPlayerContext } from '../contexts/VideoPlayerContext';

export function useVideoPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSubtitles, setShowSubtitles] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isActionPending, setIsActionPending] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { setActiveVideo } = useVideoPlayerContext();

  // Überprüfen, ob das Video bereit ist
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => {
      setIsVideoReady(true);
    };

    video.addEventListener('canplay', handleCanPlay);

    // Falls das Video bereits geladen ist
    if (video.readyState >= 3) {
      setIsVideoReady(true);
    }

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, []); // Leere Abhängigkeitsliste, da wir nur beim Mount/Unmount reagieren wollen

  const togglePlay = async () => {
    if (!videoRef.current || isActionPending) return;

    setIsActionPending(true);

    try {
      if (isPlaying) {
        await videoRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          // Stelle sicher, dass das Video bereit ist
          if (!isVideoReady) {
            return;
          }

          // Registriere dieses Video als aktives Video
          setActiveVideo(videoRef);

          // Warte mit einem Promise, um sicherzustellen, dass play() abgeschlossen wird
          await new Promise<void>((resolve, reject) => {
            const playPromise = videoRef.current?.play();

            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  setIsPlaying(true);
                  resolve();
                })
                .catch(error => {
                  console.error("Video play error:", error);
                  reject(error);
                });
            } else {
              resolve();
            }
          });

        } catch (error) {
          console.error("Video play error:", error);
          return; // Bei Fehler nicht den Status ändern
        }
      }
    } finally {
      // Längere Verzögerung, um Timing-Probleme zu vermeiden
      setTimeout(() => setIsActionPending(false), 500);
    }
  };

  const toggleSubtitles = () => {
    setShowSubtitles(!showSubtitles);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && videoRef.current.duration) {
      const currentProgress =
        (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(isNaN(currentProgress) ? 0 : currentProgress);
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (videoRef.current && videoRef.current.duration) {
      const seekTo =
        (Number(e.target.value) / 100) * videoRef.current.duration;
      videoRef.current.currentTime = seekTo;
      setProgress(Number(e.target.value));
    }
  };

  // Event-Listener für ended
  useEffect(() => {
    const video = videoRef.current;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    if (video) {
      video.addEventListener('ended', handleEnded);
      video.addEventListener('pause', handlePause);
    }

    return () => {
      if (video) {
        video.removeEventListener('ended', handleEnded);
        video.removeEventListener('pause', handlePause);
      }
    };
  }, []); // Leere Abhängigkeitsliste, da wir nur beim Mount/Unmount reagieren wollen

  return {
    videoRef,
    isPlaying,
    progress,
    volume,
    showSubtitles,
    isVideoReady,
    togglePlay,
    toggleSubtitles,
    handleTimeUpdate,
    handleVolumeChange,
    handleSeek,
  };
} 